/**
 * De eigen catalogi (issue 135, `eigen-catalogi.md`).
 *
 * Twee oppervlakken, en ze lopen bewust uiteen:
 *
 *  - **Beheer** (`/api/v1/catalogs`, achter `tenant_config.*`) ziet élke catalogus, ook een
 *    concept en ook een achter een toegangsgroep waar de beheerder zelf niet in zit.
 *  - **De lerende** (`/api/v1/my-catalogs`) ziet alleen wat gepubliceerd is, wat hij mag
 *    openen, en wat voor hém niet leeg is.
 *
 * ⚠️ DE TWEE HEBBEN EEN EIGEN TYPE. `Catalog` draagt `membership` en `access_group_id`, en dat
 * is inrichting: het zegt hoe de selectie werkt en op wie hij mikt. Eén type met optionele
 * velden zou dat onderscheid aan een vraagteken ophangen dat iemand later wegpoetst.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import type { LearningActivity } from '@shared/hooks/useLearningActivities';

/**
 * ⚠️ UIT DE GEGENEREERDE TYPES, NIET MET DE HAND OVERGESCHREVEN. `generated.d.ts` komt uit de
 * PHP-enums (`make typescript-transform`, bewaakt in `backend.yml`), dus een waarde die daar
 * bijkomt of verdwijnt komt hier vanzelf mee. Hier stond eerst een kopie van alle zes — die
 * compileert net zo goed en loopt stil uit de pas zodra iemand aan de enum komt. Zelfde keuze
 * als `useLearningActivities.ts`.
 */
export type CatalogStatus = App.Catalog.Enums.CatalogStatus;
export type CatalogViewMode = App.Catalog.Enums.CatalogViewMode;
export type CatalogLayout = App.Catalog.Enums.CatalogLayout;
export type CatalogImageRatio = App.Catalog.Enums.CatalogImageRatio;
export type CatalogVisibilityMode = App.Catalog.Enums.VisibilityMode;
export type CatalogItemMode = App.Catalog.Enums.CatalogItemMode;

/** Eén voorwaarde binnen een regel-term: een veld en de waarden die tellen (OF binnen het atoom). */
export interface MembershipAtom {
  field: string;
  values: string[];
}

/**
 * Eén term. `all` matcht alles, `rule` is een EN-reeks atomen, `catalog` neemt de inhoud van
 * een andere catalogus over — dynamisch, dus hij groeit mee.
 */
export interface MembershipTerm {
  kind: 'all' | 'rule' | 'catalog';
  atoms?: MembershipAtom[];
  catalog_id?: string | null;
}

export interface Membership {
  include: MembershipTerm[];
  exclude: MembershipTerm[];
}

export interface CatalogItemRef {
  learning_activity_id: string;
  mode: CatalogItemMode;
}

/** Een catalogus zoals het beheer hem ziet. */
export interface Catalog {
  id: string;
  name: Record<string, string>;
  description: Record<string, string> | null;
  icon: string | null;
  slug: string;
  position: number;
  in_main_nav: boolean;
  status: CatalogStatus;
  show_when_empty: boolean;
  view_mode: CatalogViewMode;
  default_layout: CatalogLayout;
  default_sort: string;
  shown_facets: string[] | null;
  image_aspect_ratio: CatalogImageRatio;
  visibility_mode: CatalogVisibilityMode;
  access_group_id: string | null;
  membership: Membership;
  items?: CatalogItemRef[];
  /** Lidmaatschap ∩ gepubliceerd — kijker-onafhankelijk, anders zegt het getal niets. */
  item_count?: number;
  /** Een dode verwijzing laat de catalogus dichtvallen; het scherm hoort dat te tonen. */
  is_broken?: boolean;
  created_at: string | null;
  updated_at: string | null;
}

/** Een catalogus zoals de lerende hem ziet: geen selectie, geen doelgroep. */
export interface CatalogMenuEntry {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  slug: string;
  position: number;
  in_main_nav: boolean;
  view_mode: CatalogViewMode;
  default_layout: CatalogLayout;
  default_sort: string;
  shown_facets: string[] | null;
  image_aspect_ratio: CatalogImageRatio;
}

export type CatalogWritable = Partial<
  Pick<
    Catalog,
    | 'name' | 'description' | 'icon' | 'slug' | 'position' | 'in_main_nav' | 'status'
    | 'show_when_empty' | 'view_mode' | 'default_layout' | 'default_sort' | 'shown_facets'
    | 'image_aspect_ratio' | 'visibility_mode' | 'access_group_id' | 'membership'
  >
> & { items?: CatalogItemRef[] };

const ADMIN_KEY = ['tenant', 'catalogs'] as const;
const MENU_KEY = ['tenant', 'my-catalogs'] as const;

/** De beheerlijst. */
export function useCatalogs(enabled = true) {
  return useQuery({
    queryKey: ADMIN_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ data: Catalog[] }>('/api/v1/catalogs');
      return data.data;
    },
    enabled,
  });
}

export function useCatalog(id: string | undefined) {
  return useQuery({
    queryKey: [...ADMIN_KEY, id],
    queryFn: async () => {
      const { data } = await api.get<{ data: Catalog }>(`/api/v1/catalogs/${id}`);
      return data.data;
    },
    enabled: Boolean(id),
  });
}

/**
 * Het menu van déze gebruiker. Lang vers houden heeft weinig zin — de inhoud hangt aan wat er
 * in de catalogi zit — maar elke paginawissel opnieuw vragen is zonde: het antwoord kost de
 * server een vraag per catalogus.
 */
export function useCatalogMenu(enabled = true) {
  return useQuery({
    queryKey: MENU_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ data: CatalogMenuEntry[] }>('/api/v1/my-catalogs');
      return data.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Eén catalogus-pagina op slug, voor de kop en de presentatie-instellingen. */
export function useCatalogBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: [...MENU_KEY, slug],
    queryFn: async () => {
      const { data } = await api.get<{ data: CatalogMenuEntry }>(`/api/v1/my-catalogs/${slug}`);
      return data.data;
    },
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ADMIN_KEY });
  // Het menu verandert mee: een catalogus die gepubliceerd wordt of leegloopt, hoort meteen
  // te verschijnen of te verdwijnen.
  void qc.invalidateQueries({ queryKey: MENU_KEY });
}

export function useCreateCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CatalogWritable) => {
      const { data } = await api.post<{ data: Catalog }>('/api/v1/catalogs', payload);
      return data.data;
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: CatalogWritable & { id: string }) => {
      const { data } = await api.put<{ data: Catalog }>(`/api/v1/catalogs/${id}`, payload);
      return data.data;
    },
    onSuccess: () => invalidate(qc),
  });
}

/**
 * Verwijderen. Is het de laatste, dan weigert de server met een 409 en een melding — de
 * aanroeper toont die als waarschuwing en probeert het daarna met `confirm`. Zo is het
 * weggooien van de laatste catalogus mogelijk maar nooit ongemerkt.
 */
export function useDeleteCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, confirm }: { id: string; confirm?: boolean }) => {
      await api.delete(`/api/v1/catalogs/${id}${confirm ? '?confirm=1' : ''}`);
    },
    onSuccess: () => invalidate(qc),
  });
}

/**
 * Waar wordt deze catalogus gebruikt (issue 1326)? Op id: het menu, de startpagina van `/catalog`, en andere catalogi
 * die hem in- of uitsluiten. Op adres: teksten en eigen mailsjablonen die naar `/catalog/<slug>` linken.
 */
export interface CatalogReferences {
  in_menu: boolean;
  is_start: boolean;
  catalogs: Array<{ id: string; name: string; slug: string; mode: 'include' | 'exclude' }>;
  activities: Array<{ id: string; title: string }>;
  catalog_texts: Array<{ id: string; name: string; slug: string }>;
  texts: Array<{ locale: string; key: string }>;
  /** De eigen mailsjablonen van de omgeving die ernaar linken, met de talen waarin dat gebeurt. */
  mail_templates: Array<{ slug: string; name: string; locales: string[] }>;
}

/**
 * De verwijzingen ophalen op het moment van de handeling (verwijderen, een nieuw adres), niet bij het laden van de
 * lijst: een vraag per catalogus voor een antwoord dat alleen bij die ene klik telt, is zonde. Altijd vers.
 */
export function useCatalogReferencesFetcher() {
  const qc = useQueryClient();
  return (id: string) => qc.fetchQuery({
    queryKey: [...ADMIN_KEY, id, 'references'],
    queryFn: async () => {
      const { data } = await api.get<{ data: CatalogReferences }>(`/api/v1/catalogs/${id}/references`);
      return data.data;
    },
    staleTime: 0,
  });
}

export function useReorderCatalogs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data } = await api.put<{ data: Catalog[] }>('/api/v1/catalogs/reorder', { ids });
      return data.data;
    },
    onSuccess: () => invalidate(qc),
  });
}

/** De trede uit de voorrangsladder plus de term die besliste; zie `MembershipReason` (backend). */
export interface MembershipReasonPayload {
  rung: 1 | 2 | 3 | 4 | 5;
  term_index: number | null;
  matching_terms: number;
  broken: boolean;
  is_member: boolean;
}

export interface CatalogPreview {
  data: Array<{ id: string; title: string; status: string; type: string }>;
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    as: 'holds' | 'learner';
    is_broken: boolean;
  };
  reasons: Record<string, MembershipReasonPayload>;
}

/**
 * Wat zit er in deze catalogus?
 *
 * ⚠️ TWEE STANDEN, EN HET VERSCHIL IS NIET COSMETISCH. `holds` toont wat de **selectie**
 * oplevert, los van wie kijkt en los van status — dat is wat je instelt. `learner` haalt alles
 * door de hele poortstapel met de rechten van de kijker. Eén stand zou een proefweergave geven
 * die zichzelf bevestigt: voor een bewerker keren de status-, zichtbaarheids- én bron-poort
 * alle drie vroeg terug, dus "zoals een lerende het ziet" is voor hém niemand.
 */
export function useCatalogPreview(id: string | undefined, as: 'holds' | 'learner' = 'holds') {
  return useQuery({
    queryKey: [...ADMIN_KEY, id, 'preview', as],
    queryFn: async () => {
      const { data } = await api.get<CatalogPreview>(`/api/v1/catalogs/${id}/preview?as=${as}`);
      return data;
    },
    enabled: Boolean(id),
  });
}

/** Eén regel van de omgekeerde lijst: deze catalogus, en waarom de training er wel/niet in staat. */
export interface ActivityCatalogRow {
  id: string;
  name: string;
  slug: string;
  status: CatalogStatus;
  in_main_nav: boolean;
  reason: MembershipReasonPayload;
}

/**
 * De andere kant op: in welke catalogi staat déze training, en waardoor (issue 135)?
 *
 * ⚠️ HET ANTWOORD GAAT OVER HET LIDMAATSCHAP, NIET OVER ZICHTBAARHEID. Een training die er
 * volgens de ladder in hoort, kan voor een lerende alsnog ontbreken omdat ze concept is, buiten
 * zijn doelgroep valt of haar bron weg is. Het paneel zegt dat erbij; zonder die zin leest de
 * lijst als "hier zien mijn mensen hem", en dat is een andere vraag.
 *
 * Achter `tenant_config.view` of `activity.edit` op de server; de aanroeper houdt de hook uit
 * met `enabled` zodat een scherm zonder dat recht geen 403 ophaalt om hem daarna te verbergen.
 */
export function useActivityCatalogs(activityId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...ADMIN_KEY, 'for-activity', activityId],
    queryFn: async () => {
      const { data } = await api.get<{ data: ActivityCatalogRow[] }>(
        `/api/v1/learning-activities/${activityId}/catalogs`,
      );
      return data.data;
    },
    enabled: enabled && Boolean(activityId),
  });
}

/** Eén kaart in de uitvoeringen-weergave: de bijeenkomst, plus de training eromheen. */
export interface CatalogOccurrence {
  id: string;
  /** Alleen gevuld als hij van de training-titel verschilt (`distinctTitle()`). */
  title: string | null;
  description: string | null;
  language: string | null;
  delivery_mode: string | null;
  status: string;
  duration_minutes: number | null;
  max_participants: number | null;
  moments: Array<{
    id: string;
    date: string | null;
    start_time: string | null;
    end_time: string | null;
    location: string | null;
  }>;
  /** Dezelfde vorm als een kaart in de trainingen-weergave — één kaart-component, niet twee. */
  activity: LearningActivity;
}

export interface CatalogOccurrencePage {
  data: CatalogOccurrence[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    /**
     * De taal-facet van déze lijst, geteld vóór het taalfilter — zoals elke facet, zodat de
     * andere talen zichtbaar blijven zodra je er één kiest. Hij hoort hier en niet bij de
     * trainingen-tellingen: dezelfde training in twee talen is in deze weergave twee kaarten.
     */
    language_counts: Record<string, number>;
  };
}

/**
 * De uitvoeringen binnen één catalogus (`view_mode = occurrences`, issue 135).
 *
 * ⚠️ `q` EN `language` SLAAN HIER OP DE UITVOERING, niet op de training. Dat is de hele reden
 * dat deze weergave bestaat: dezelfde training in het Nederlands en het Engels is hier twee
 * kaarten, en een taalfilter op de training zou ze allebei weghalen of allebei binnenhalen.
 * De andere filters van de bladerkant gelden hier niet — ze beschrijven de training, en die
 * staat al vast zodra je naar haar uitvoeringen kijkt.
 */
export function useCatalogOccurrences(
  slug: string | undefined,
  filters: { q?: string; language?: string[] } = {},
) {
  const q = (filters.q ?? '').trim();
  const language = filters.language ?? [];

  return useInfiniteQuery({
    queryKey: [...MENU_KEY, slug, 'occurrences', q, [...language].sort()],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number | string[]> = { page: pageParam, per_page: 24 };
      if (q !== '') params.q = q;
      if (language.length > 0) params.language = language;

      const { data } = await api.get<CatalogOccurrencePage>(
        `/api/v1/my-catalogs/${slug}/occurrences`,
        { params },
      );
      return data;
    },
    getNextPageParam: (last) => (last.meta.current_page < last.meta.last_page ? last.meta.current_page + 1 : undefined),
    enabled: Boolean(slug),
  });
}
